import { Troubleshoot } from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import {
  investigationShowCarAtom,
  investigationShowDetourAtom,
  investigationShowNoGpsAtom,
  investigationShowPlannedAtom,
  investigationShowStationsAtom,
  investigationShowTrackingAtom,
  investigationStationLabelsAtom,
} from "@ryde/shared";

import {
  STATION_COLOR,
  TRAIL_ACTUAL_COLOR,
  TRAIL_DETOUR_COLOR,
  TRAIL_NO_GPS_COLOR,
  TRAIL_ROUTE_COLOR,
} from "./investigation.constants";
import {
  InvestigationTopBarStyled,
  InvestigationTopBarTitleStyled,
  ToggleGroupStyled,
} from "./investigation.workspace.styled";
import { FilterToggle } from "./parts/investigation.filter-toggle";
import { usePlaybackContext } from "./playback/ride-investigation-playback.context";

/** Map top bar: ride title + layer toggles (planned / real GPS / no signal / detour / stations / numbers). */
export const InvestigationTopBar: FC = () => {
  const { model } = usePlaybackContext();
  const { t } = useTranslation();
  const [showPlanned, setShowPlanned] = useRecoilState(
    investigationShowPlannedAtom,
  );
  const [showTracking, setShowTracking] = useRecoilState(
    investigationShowTrackingAtom,
  );
  const [showNoGps, setShowNoGps] = useRecoilState(investigationShowNoGpsAtom);
  const [showDetour, setShowDetour] = useRecoilState(
    investigationShowDetourAtom,
  );
  const [showStations, setShowStations] = useRecoilState(
    investigationShowStationsAtom,
  );
  const [showCar, setShowCar] = useRecoilState(investigationShowCarAtom);
  const [stationLabels, setStationLabels] = useRecoilState(
    investigationStationLabelsAtom,
  );

  const resolveTitle = (): string => {
    if (!model) {
      return "";
    }

    return model.payload.summary.title?.trim() || `#${model.rideId}`;
  };

  const title = resolveTitle();
  const filter = (key: string): string =>
    t(`rideMap.investigation.filters.${key}`);

  return (
    <InvestigationTopBarStyled>
      <InvestigationTopBarTitleStyled>
        <Troubleshoot fontSize="small" />
        <span>{`${t("rideMap.investigation.title")}${title ? ` · ${title}` : ""}`}</span>
      </InvestigationTopBarTitleStyled>
      <ToggleGroupStyled>
        <FilterToggle
          active={showPlanned}
          color={TRAIL_ROUTE_COLOR}
          label={filter("planned")}
          swatch="line-solid"
          onToggle={() => setShowPlanned(value => !value)}
        />
        <FilterToggle
          active={showTracking}
          color={TRAIL_ACTUAL_COLOR}
          label={filter("tracking")}
          swatch="line-solid"
          onToggle={() => setShowTracking(value => !value)}
        />
        <FilterToggle
          active={showNoGps}
          color={TRAIL_NO_GPS_COLOR}
          label={filter("noSignal")}
          swatch="line-dotted"
          onToggle={() => setShowNoGps(value => !value)}
        />
        <FilterToggle
          active={showDetour}
          color={TRAIL_DETOUR_COLOR}
          label={filter("detour")}
          swatch="line-dashed"
          onToggle={() => setShowDetour(value => !value)}
        />
        <FilterToggle
          active={showCar}
          color={TRAIL_ACTUAL_COLOR}
          label={filter("car")}
          swatch="dot"
          onToggle={() => setShowCar(value => !value)}
        />
        <FilterToggle
          active={showStations}
          color={STATION_COLOR}
          label={filter("stations")}
          swatch="dot"
          onToggle={() => setShowStations(value => !value)}
        />
        <FilterToggle
          active={stationLabels}
          color={STATION_COLOR}
          label={filter("numbers")}
          swatch="none"
          disabled={!showStations}
          onToggle={() => setStationLabels(value => !value)}
        />
      </ToggleGroupStyled>
    </InvestigationTopBarStyled>
  );
};
